import log from '@/lib/logger';
import { type AppDispatch, type RootState } from '@/store';
import { type UnknownAction } from '@reduxjs/toolkit';
import { getCollectionNotes, getCollectionNotesSummary } from 'services/chargePoints';
import type { CollectionNote, CollectionNoteSummary } from 'types/swagger';

export interface FilterCollectionNoteType {
  timestamp_start: string;
  timestamp_end: string;
  user_group_id?: number;
  contract_id?: number;
}

enum CollectionNotesActionTypes {
  SET_COLLECTION_NOTES = 'collection_notes/set_collection_notes',
  SET_COLLECTION_NOTES_SUMMARY = 'collection_notes/set_collection_notes_summary',
  SET_IS_FETCHING_COLLECTION_NOTES = 'collection_notes/set_is_fetching_collection_notes',
  SET_IS_FETCHING_COLLECTION_NOTES_SUMMARY = 'collection_notes/set_is_fetching_collection_notes_summary',
}

export const setCollectionNotes = (collectionNotes: CollectionNote[]) => ({
  type: CollectionNotesActionTypes.SET_COLLECTION_NOTES,
  payload: collectionNotes,
});

export const setCollectionNotesSummary = (summary: CollectionNoteSummary) => ({
  type: CollectionNotesActionTypes.SET_COLLECTION_NOTES_SUMMARY,
  payload: summary,
});

export const setIsFetchingCollectionNotes = (isFetching: boolean) => ({
  type: CollectionNotesActionTypes.SET_IS_FETCHING_COLLECTION_NOTES,
  payload: isFetching,
});

export const setIsFetchingCollectionNotesSummary = (isFetching: boolean) => ({
  type: CollectionNotesActionTypes.SET_IS_FETCHING_COLLECTION_NOTES_SUMMARY,
  payload: isFetching,
});

interface CollectionNoteAction {
  type: CollectionNotesActionTypes;
  payload: any;
}

interface CollectionNotesState {
  collectionNotes: {
    all: CollectionNote[];
    byId: Record<number, CollectionNote>;
    summary: CollectionNoteSummary;
  };
  isFetchingCollectionNotes: boolean;
  isFetchingCollectionNotesSummary: boolean;
}

const INIT_STATE: CollectionNotesState = {
  collectionNotes: {
    all: [],
    byId: {},
    summary: {} as CollectionNoteSummary,
  },
  isFetchingCollectionNotes: false,
  isFetchingCollectionNotesSummary: false,
};

export default function collectionNoteReducer(state = INIT_STATE, action: UnknownAction) {
  const { type, payload } = action;

  switch (type) {
    case CollectionNotesActionTypes.SET_COLLECTION_NOTES: {
      return {
        ...state,
        collectionNotes: {
          ...state.collectionNotes,
          all: payload as CollectionNote[],
          byId: payload
            ? Object.fromEntries(
                (payload as CollectionNote[]).map((collectionNote) => [
                  collectionNote.id,
                  collectionNote,
                ])
              )
            : {},
        },
      };
    }

    case CollectionNotesActionTypes.SET_COLLECTION_NOTES_SUMMARY: {
      return {
        ...state,
        collectionNotes: {
          ...state.collectionNotes,
          summary: payload as CollectionNoteSummary,
        },
      };
    }

    case CollectionNotesActionTypes.SET_IS_FETCHING_COLLECTION_NOTES: {
      return {
        ...state,
        isFetchingCollectionNotes: payload as boolean,
      };
    }

    case CollectionNotesActionTypes.SET_IS_FETCHING_COLLECTION_NOTES_SUMMARY: {
      return {
        ...state,
        isFetchingCollectionNotesSummary: payload as boolean,
      };
    }

    default: {
      return state;
    }
  }
}

export const fetchCollectionNotes =
  (filter?: FilterCollectionNoteType) => async (dispatch: AppDispatch) => {
    dispatch(setIsFetchingCollectionNotes(true));

    try {
      const collectionNotes = await getCollectionNotes(filter);

      if (collectionNotes.data) {
        dispatch(setCollectionNotes(collectionNotes.data.results));
      }

      return collectionNotes;
    } catch (error) {
      log.error(error);
    } finally {
      dispatch(setIsFetchingCollectionNotes(false));
    }
  };

export const fetchCollectionNotesSummary =
  (filter?: FilterCollectionNoteType) => async (dispatch: AppDispatch) => {
    dispatch(setIsFetchingCollectionNotesSummary(true));

    try {
      const summary = await getCollectionNotesSummary(filter);

      if (summary.data) {
        dispatch(setCollectionNotesSummary(summary.data));
      }

      return summary;
    } catch (error) {
      log.error(error);
    } finally {
      dispatch(setIsFetchingCollectionNotesSummary(false));
    }
  };

export const selectAllCollectionNotes = (state: RootState) =>
  state.collectionNotes.collectionNotes.all;
export const selectCollectionNotesById = (state: RootState) =>
  state.collectionNotes.collectionNotes.byId;
export const selectCollectionNotesSummary = (state: RootState) =>
  state.collectionNotes.collectionNotes.summary;
export const selectIsFetchingCollectionNotes = (state: RootState) =>
  state.collectionNotes.isFetchingCollectionNotes;
export const selectIsFetchingCollectionNotesSummary = (state: RootState) =>
  state.collectionNotes.isFetchingCollectionNotesSummary;
